/**
 * Canto theme controller.
 *
 * Resolves theme from (in order):
 *   1. user profile preference (passed in by the app)
 *   2. localStorage cache
 *   3. system `prefers-color-scheme`
 *   4. 'dark' (Canto's default)
 *
 * The `applyThemeEarly` logic is duplicated as an inline blocking script
 * in `index.html` to avoid a flash of wrong theme. Changes here should
 * stay in sync with that script.
 */

const STORAGE_KEY = 'canto-theme';
const VALID: readonly ThemePreference[] = ['light', 'dark', 'system'];

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === 'light' || pref === 'dark') return pref;
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark';
}

export function applyTheme(theme: ResolvedTheme): void {
  document.documentElement.setAttribute('data-theme', theme);
}

export function getStoredPreference(): ThemePreference {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    return VALID.includes(cached as ThemePreference) ? (cached as ThemePreference) : 'system';
  } catch {
    return 'system';
  }
}

export async function setThemePreference(
  pref: ThemePreference,
  syncToProfile?: (pref: ThemePreference) => Promise<void>,
): Promise<void> {
  if (!VALID.includes(pref)) throw new Error(`Invalid theme: ${pref}`);
  localStorage.setItem(STORAGE_KEY, pref);
  applyTheme(resolveTheme(pref));
  if (syncToProfile) await syncToProfile(pref);
}

export function watchSystemTheme(): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (): void => {
    const pref = getStoredPreference();
    if (pref === 'system') applyTheme(resolveTheme('system'));
  };
  mql.addEventListener('change', handler);
  return () => mql.removeEventListener('change', handler);
}
